import { useQuery } from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { AreaUsageGroup, AreaUsagesResponse } from 'types/area-usages';
import { usageCount, usageGroups } from './area-usages';

/** Query-key prefix for an area's referencing records. */
export const AREA_USAGES_QUERY_KEY = 'area-geometries:usages' as const;

export interface AreaUsages {
  /** Referencing types with at least one record, tier 1 first. */
  groups: AreaUsageGroup[];
  /** Records referencing the area, for the callout's headline. */
  total: number;
  loading: boolean;
  // Why the check couldn't run, or null. Said out loud rather than swallowed: "no
  // references" and "we couldn't find out" are different things to act on.
  error: string | null;
}

const NO_GROUPS: AreaUsageGroup[] = [];

// Who else points at this area. Asked when a lifecycle panel opens rather than on page
// load — it is only ever read to warn someone mid-action.
export const useAreaUsages = (
  areaGeometryId: number | null | undefined,
  enabled: boolean,
  businessId?: string
): AreaUsages => {
  const { data, isFetching, error } = useQuery<AreaUsagesResponse, Error>({
    queryKey: [AREA_USAGES_QUERY_KEY, businessId ?? areaGeometryId],
    queryFn: ({ signal }) =>
      api
        .post('queries/area_geometries/usages', {
          // By business area where the payload names one — that is the stable id, and
          // what the answer is really about. An area id is resolved to the same place.
          json: businessId ? { businessId } : { areaGeometryId },
          signal,
        })
        .json<AreaUsagesResponse>(),
    enabled: enabled && !!areaGeometryId,
    retry: false,
  });

  const groups = data ? usageGroups(data) : NO_GROUPS;

  return {
    groups,
    total: usageCount(groups),
    loading: isFetching,
    error: error ? 'Could not check what references this area.' : null,
  };
};
